import React, { useState, useRef, useEffect } from 'react';
import { useConfigJapanese } from '../../contexts/ConfigContext';
import { TranslateService } from '../../services/japanese';
import { IconSvg, IconName } from '../IconSvg';
import { ToolbarButton } from '../Toolbar/ToolbarButton';
import './style.css';

// 翻译服务选项列表
const serviceOptions = [
  {
    id: TranslateService.Google,
    label: 'Google 翻译',
    icon: 'mdi:google-translate' as IconName,
    description: '谷歌提供的翻译服务'
  },
  {
    id: TranslateService.M2M100,
    label: 'M2M100 翻译',
    icon: 'carbon:machine-learning' as IconName,
    description: '基于深度学习的多语言翻译模型'
  }
];

// 提取服务选择器内容为单独组件
export const TranslateServiceContent: React.FC<{
  service: TranslateService;
  onChange: (service: TranslateService) => void;
  isToolbar?: boolean;
  closeMenu?: () => void;
}> = ({ service, onChange, isToolbar = false, closeMenu }) => {
  const handleChange = (newService: TranslateService) => {
    onChange(newService);
    if (closeMenu) {
      closeMenu();
    }
  };

  return (
    <div className={isToolbar ? 'service-options' : 'settings-service-options'}>
      {isToolbar && <div className="tooltip-header">选择翻译服务</div>}
      {serviceOptions.map((option) => (
        <button
          key={option.id}
          className={`service-option ${service === option.id ? 'active' : ''}`}
          onClick={() => handleChange(option.id)}
        >
          <IconSvg name={option.icon} width={20} height={20} />
          <div className="service-option-content">
            <span className="service-option-label">{option.label}</span>
            <span className="service-option-description">{option.description}</span>
          </div>
          {service === option.id && (
            <IconSvg name="ph:check-bold" className="service-option-check" width={16} height={16} />
          )}
        </button>
      ))}
    </div>
  );
};

// PC端的下拉菜单组件
export const TranslateServiceSelector: React.FC = () => {
  const { config, updateConfig } = useConfigJapanese();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleChange = (service: TranslateService) => {
    updateConfig({ translateService: service });
    setShowDropdown(false);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className="translate-service-selector" ref={dropdownRef}>
      <ToolbarButton
        onClick={() => setShowDropdown(!showDropdown)}
        active={showDropdown}
        hasExpandAction={true}
        tooltip={{
          title: "切换翻译服务",
          description: "选择使用的翻译服务提供商"
        }}
      >
        <IconSvg name="heroicons-solid:switch-horizontal" width={20} height={20} />
      </ToolbarButton>

      {showDropdown && (
        <div className="service-dropdown" onClick={handleDropdownClick}>
          <TranslateServiceContent
            service={config.translateService}
            onChange={handleChange}
            isToolbar={true}
          />
        </div>
      )}
    </div>
  );
};